import { SectionName } from "@/entities/dossier-settings";
import { RoleGate, UserRole } from "@/features/auth";
import { BerichtBalk } from "@/features/berichten";
import { Icon, IconName, Skeleton } from "@/shared/components";
import { cn, convertToKebabCase } from "@/shared/utils";
import { ReactNode } from "react";
import { Link } from "react-router";
import { sectionConfigMap } from "../constants";
import { useDossierId } from "../hooks";

interface DossierSummaryCardProps {
    children?: ReactNode;
    section: SectionName;
    status?: string;
    className?: string;
    ["data-testid"]?: string;
}

export function DossierSummaryCard({ children, className, section, status, "data-testid": dataTestId }: Readonly<DossierSummaryCardProps>) {
    const dossierId = useDossierId();
    const { title, icon, slug, onderwerp } = sectionConfigMap[section];

    return (
        <article
            className={cn("border-default rounded-m flex flex-col overflow-clip border", className)}
            data-testid={dataTestId ?? convertToKebabCase(title)}
        >
            <DossierSummaryCardHeader icon={icon} title={title} link={slug} status={status} />
            <RoleGate roles={[UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT, UserRole.ALLEENLEZEN]}>
                {onderwerp && <BerichtBalk dossierId={dossierId} onderwerp={onderwerp} />}
            </RoleGate>
            <div className="pt-2 pb-3">{children}</div>
        </article>
    );
}

export function DossierSummaryCardSkeleton({ className, section, status, children }: Readonly<DossierSummaryCardProps>) {
    const { title, icon, slug } = sectionConfigMap[section];
    return (
        <article
            className={cn("border-default rounded-m flex flex-col overflow-clip border pb-3", className)}
            data-testid={convertToKebabCase(title)}
        >
            <DossierSummaryCardHeader title={title} link={slug} icon={icon} status={status} />
            <div className="col-start-2 px-3">{children || <Skeleton className="h-10 w-full" />}</div>
        </article>
    );
}

type DossierDetailsCardHeaderProps = {
    title: string;
    link?: string;
    icon: IconName;
    status?: string;
};

function DossierSummaryCardHeader({ title, link, icon, status }: Readonly<DossierDetailsCardHeaderProps>) {
    if (link) {
        return (
            <Link to={link} className="bg-elevated-2 grid w-full grid-cols-[min-content_1fr_min-content] items-center gap-x-2 p-3">
                <Icon name={icon} className="text-content-emphasis" />
                <div className="flex flex-row justify-between">
                    <h2 className="text-content-emphasis text-l font-medium" data-testid="dossier-summary-title">
                        {title}
                    </h2>
                    <p className="text-content-subtle text-m font-normal" data-testid="dossier-summary-status">
                        {status}
                    </p>
                </div>
                <Icon name="chevron-right" className="text-content-interactive-default" />
            </Link>
        );
    }

    return (
        <div className="bg-elevated-2 grid w-full grid-cols-[min-content_1fr_min-content] items-center gap-x-2 p-3">
            <Icon name={icon} className="text-content-emphasis" />
            <h2 className="text-content-emphasis text-l font-medium">{title}</h2>
        </div>
    );
}
